import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { MessageCircle, Send } from 'lucide-react';
import { contactByLanguage } from '../data/mock';

const WhatsAppForm = ({ variant = 'default' }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    message: '',
  });
  const [rgpdAccepted, setRgpdAccepted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Get WhatsApp number based on language
    const currentContact = contactByLanguage[language] || contactByLanguage.es;
    
    // Build WhatsApp message
    let message = t.form.whatsappMessage
      .replace('{name}', formData.name || '-')
      .replace('{company}', formData.company || '-')
      .replace('{website}', formData.website || 'No especificado')
      .replace('{message}', formData.message || '-')
      .replace('{phone}', formData.phone || '-');

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // GTM Conversion Event - Form Submit
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'form_submit_whatsapp',
        form_name: 'contact_form',
        form_company: formData.company || '',
        form_has_website: !!formData.website,
        language: language
      });
    }
    
    // Open WhatsApp (no lead saved to database)
    window.open(`https://wa.me/${currentContact.whatsapp}?text=${encodedMessage}`, '_blank');
  };

  const isCompact = variant === 'compact';

  // RGPD text by language
  const rgpdText = {
    es: 'Acepto la política de privacidad y el tratamiento de mis datos personales.',
    fr: "J'accepte la politique de confidentialité et le traitement de mes données personnelles.",
    en: 'I accept the privacy policy and the processing of my personal data.'
  };

  const rgpdLink = {
    es: '/es/privacidad',
    fr: '/fr/privacidad',
    en: '/en/privacidad'
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="whatsapp-form">
      <div className={`grid gap-4 ${isCompact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-slate-700">
            {t.form.fields.name}
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Juan García"
            data-testid="form-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company" className="text-sm font-medium text-slate-700">
            {t.form.fields.company}
          </Label>
          <Input
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Mi Empresa S.L."
            data-testid="form-company"
          />
        </div>
      </div>

      <div className={`grid gap-4 ${isCompact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-slate-700">
            {t.form.fields.email}
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500"
            placeholder="juan@empresa.com"
            data-testid="form-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
            {t.form.fields.phone}
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500"
            placeholder="+34 600 000 000"
            data-testid="form-phone"
          />
        </div>
      </div>

      {!isCompact && (
        <div className="space-y-2">
          <Label htmlFor="website" className="text-sm font-medium text-slate-700">
            {t.form.fields.website}
          </Label>
          <Input
            id="website"
            name="website"
            type="url"
            value={formData.website}
            onChange={handleChange}
            className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500"
            placeholder="https://miempresa.com"
            data-testid="form-website"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="message" className="text-sm font-medium text-slate-700">
          {t.form.fields.message}
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={isCompact ? 3 : 4}
          className="bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
          placeholder="Cuéntanos sobre tu proyecto..."
          data-testid="form-message"
        />
      </div>

      {/* RGPD Checkbox */}
      <div className="flex items-start space-x-3">
        <Checkbox
          id="rgpd"
          checked={rgpdAccepted}
          onCheckedChange={setRgpdAccepted}
          className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
          data-testid="form-rgpd"
        />
        <Label htmlFor="rgpd" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
          {rgpdText[language] || rgpdText.es}{' '}
          <a 
            href={rgpdLink[language] || rgpdLink.es} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {t.footer.privacy}
          </a>
        </Label>
      </div>

      <Button
        type="submit"
        disabled={!rgpdAccepted}
        className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 shadow-lg hover:shadow-xl transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
        size="lg"
        data-testid="form-submit"
      >
        <MessageCircle className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
        {t.form.submit}
        <Send className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    </form>
  );
};

export default WhatsAppForm;
